import React from "react";
import { Box, Typography, useTheme } from "@mui/material";

const VariantRadioGroup = ({ label, value, onChange, variants = [] }) => {
  const theme = useTheme();
  const tc = theme.customColors;

  if (!variants.length) return null;

  const handleSelect = (variant) => {
    if (variant.disabled) return;
    onChange(variant.key);
  };

  return (
    <Box sx={{ mb: 2.5 }}>
      {label && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: tc.text.strong,
            mb: 1.5,
          }}
        >
          {label}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {variants.map((variant) => {
          const disabled = variant.disabled === true;
          const selected = value === variant.key;
          return (
            <Box
              key={variant.key}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-disabled={disabled}
              aria-pressed={selected}
              onClick={() => handleSelect(variant)}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelect(variant);
                }
              }}
              sx={{
                p: 2,
                borderRadius: "8px",
                backgroundColor: selected
                  ? tc.background.infoBox
                  : tc.background.cardHeader,
                border: `1px solid ${
                  selected ? tc.status.info.main : tc.border.main
                }`,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                transition: "background-color 120ms, border-color 120ms",
                "&:focus-visible": {
                  outline: `2px solid ${tc.status.info.main}`,
                  outlineOffset: "2px",
                },
              }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  mt: "2px",
                  borderRadius: "50%",
                  border: `2px solid ${
                    selected ? tc.status.info.main : tc.border.strong
                  }`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {selected && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: tc.status.info.main,
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: variant.description ? 0.5 : 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: tc.text.strong,
                    }}
                  >
                    {variant.label}
                  </Typography>
                  {variant.recommended && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: "4px",
                        backgroundColor: tc.status.info.bg,
                        color: tc.status.info.text,
                        fontWeight: 600,
                        fontSize: "11px",
                      }}
                    >
                      Recommended
                    </Typography>
                  )}
                </Box>
                {variant.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: tc.text.secondary,
                      fontSize: "0.8125rem",
                    }}
                  >
                    {variant.description}
                  </Typography>
                )}
                {disabled && variant.disabledReason && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.75,
                      color: tc.status.warning.text,
                      fontWeight: 500,
                    }}
                  >
                    {variant.disabledReason}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default VariantRadioGroup;
